import { createSlice } from "@reduxjs/toolkit";
import {
  requestPresignedUrl,
  uploadFileToS3,
  fetchCallLogSummary,
  fetchUploadedCallLogs,
  deleteCallLog,
  downloadCallLog
} from "./thunk";

export const initialState = {
  uploadedCallLogs: [],
  totalCount: 0,
  currentCallLog: null,
  uploadProgress: 0,
  callLogSummary: null,
  preSignedData: null,
  listLoading: false,
  summaryLoading: false,
  deleteLoading: false,
  loading: false,
  error: null
};

const CallLogSlice = createSlice({
  name: 'CallLog',
  initialState,
  reducers: {
    // Custom reducer for upload progress
    uploadProgress: (state, action) => {
      state.uploadProgress = action.payload;
    },
    resetUpload: (state) => {
      state.currentCallLog = null;
      state.uploadProgress = 0;
      state.preSignedData = null;
    },
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    // Request Pre-signed URL
    builder.addCase(requestPresignedUrl.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(requestPresignedUrl.fulfilled, (state, action) => {
      state.preSignedData = action?.payload;
      state.loading = false;
    });
    builder.addCase(requestPresignedUrl.rejected, (state, action) => {
      state.error = action?.payload || 'Failed to get upload URL';
      state.loading = false;
    });

    // Upload File to S3
    builder.addCase(uploadFileToS3.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(uploadFileToS3.fulfilled, (state, action) => {
      state.currentCallLog = action?.payload;
      state.loading = false;
      state.uploadProgress = 100;
    });
    builder.addCase(uploadFileToS3.rejected, (state, action) => {
      state.error = action?.payload || 'Upload failed';
      state.loading = false;
    });

    // Fetch Call Log Summary
    builder.addCase(fetchCallLogSummary.pending, (state) => {
      state.summaryLoading = true;
    });
    builder.addCase(fetchCallLogSummary.fulfilled, (state, action) => {
      state.callLogSummary = action?.payload.summary;
      state.summaryLoading = false;
    });
    builder.addCase(fetchCallLogSummary.rejected, (state, action) => {
      state.error = action?.payload || 'Call log summary fetch failed';
      state.summaryLoading = false;
    });

    // Fetch Uploaded Call Logs
    builder.addCase(fetchUploadedCallLogs.pending, (state) => {
      state.listLoading = true;
      state.error = null; // Clear error when starting a new fetch
    });
    builder.addCase(fetchUploadedCallLogs.fulfilled, (state, action) => {
      state.uploadedCallLogs = action?.payload?.summaries || [];
      state.totalCount = action?.payload?.total_count || 0;
      state.listLoading = false;
      state.error = null; // Clear error on success
    });
    builder.addCase(fetchUploadedCallLogs.rejected, (state, action) => {
      state.error = action?.payload || 'Failed to load call logs';
      state.listLoading = false;
    });

    // Delete Call Log
    builder.addCase(deleteCallLog.pending, (state) => {
      state.deleteLoading = true;
    });
    builder.addCase(deleteCallLog.fulfilled, (state, action) => {
      state.uploadedCallLogs = state.uploadedCallLogs.filter(
        callLog => callLog.call_log_id !== action?.payload?.callLogId
      );
      state.deleteLoading = false;
    });
    builder.addCase(deleteCallLog.rejected, (state, action) => {
      state.error = action?.payload || 'Delete failed';
      state.deleteLoading = false;
    });

    // Download Call Log
    builder.addCase(downloadCallLog.rejected, (state, action) => {
      state.error = action?.payload || 'Download failed';
    });
  }
});

export const { uploadProgress, resetUpload, clearError } = CallLogSlice.actions;
export default CallLogSlice.reducer;
